<template>
  <div ref="opinionBar"
    class="opinion-bar"
    :class="{
      ['opinion-bar--sticky']: shouldStick,
      ['opinion-bar--' + position]: true,
      ['opinion-bar--authenticated']: isAuthorized,
      ['opinion-bar--provider']: isProviderUser,
      }"
  >
    <div ref="sensor" class="sensor"></div>
    <opinion-bar-button-wrapper
      class="option"
      tooltip-label="Parent opinion"
      tooltip-text="This is where a parent will indicate how
      well your facility matches what they’re looking for."
    >
      <button
        class="opinion-bar__button"
        :class="{'opinion-bar__button--favorited': isFavorited}"
        v-on="!isPreviewMode ? { click: toggleFavorited } : {}"
      >
        <i class="fal fa-heart" :class="{'fas': isFavorited}"></i>
        <span>Great fit!</span>
      </button>
    </opinion-bar-button-wrapper>
    <opinion-bar-button-wrapper
      tooltip-label="Parent opinion"
      tooltip-text="This is where a parent will indicate how
      well your facility matches what they’re looking for."
    >
      <button
        class="opinion-bar__button"
        :class="{'opinion-bar__button--saved': isSaved}"
        v-on="!isPreviewMode ? { click: toggleSaved } : {}"
      >
        <i class="fal fa-thumbs-up" :class="{'fas': isSaved}"></i>
        <span>Maybe</span>
      </button>
    </opinion-bar-button-wrapper>
    <opinion-bar-button-wrapper
      tooltip-label="Parent opinion"
      tooltip-text="This is where a parent will indicate how
      well your facility matches what they’re looking for."
    >
      <button
        class="opinion-bar__button"
        :class="{'opinion-bar__button--ignored': isIgnored}"
        v-on="!isPreviewMode ? { click: toggleIgnored } : {}"
      >
        <i class="fal fa-thumbs-down" :class="{'fas': isIgnored}"></i>
        <span>Not a fit</span>
      </button>
    </opinion-bar-button-wrapper>
  </div>
</template>

<script>
import { getEnvVar } from '@utils';
import OpinionBarButtonWrapper from '@components/opinion-bar/opinion-bar-button-wrapper.vue';

export default {
  components: {
    OpinionBarButtonWrapper,
  },
  props: {
    position: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'bottom'].includes(value),
    },
    isFavorited: {
      type: Boolean,
    },
    isSaved: {
      type: Boolean,
    },
    isIgnored: {
      type: Boolean,
    },
    sticky: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      shouldStick: false,
      observer: null,
    };
  },
  computed: {
    isPreviewMode() {
      return this.$store.getters.isPreviewMode;
    },
    isAuthorized() {
      return getEnvVar('AUTHORIZED');
    },
    isProviderUser() {
      return this.$store.state.user.current.isProvider;
    },
  },
  methods: {
    toggleFavorited() {
      return this.$emit('toggle-opinion', 'Favorited');
    },
    toggleSaved() {
      return this.$emit('toggle-opinion', 'Saved');
    },
    toggleIgnored() {
      const currentRoute = this.$route.name;
      if (currentRoute === 'facility-profile') {
        this.$emit('toggle-opinion', 'Ignored');
        return;
      }

      this.$store.dispatch('facilities/flashOpinionBar');
      this.$emit('toggle-opinion', 'Ignored');
    },
  },
  created() {
    if (!this.sticky) {
      return;
    }

    this.observer = new IntersectionObserver(
      ([e]) => {
        if (e.intersectionRatio === 0) {
          this.shouldStick = true;
        } else if (e.intersectionRatio !== 0) {
          this.shouldStick = false;
        }
      },
      { threshold: [0, 1] },
    );
  },
  mounted() {
    this.observer?.observe(this.$refs.sensor);
  },
  beforeUnmount() {
    this.observer?.disconnect();
  },
};
</script>

<style lang="scss" scoped>
.opinion-bar {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  border-bottom-left-radius: calc(var(--grid-unit) * 2);
  border-bottom-right-radius: calc(var(--grid-unit) * 2);
  background: #fff;
  position: sticky;
  top: var(--nav-height);
  z-index: 1;
  height: calc(var(--grid-unit) * 6);

  @media (min-width: 768px) {
    top: calc(var(--nav-height) + var(--grid-unit));
  }

  .sensor {
    top: -66px;
    position: absolute;
  }

  .option {
    grid-column-start:1;
  }

  &--sticky {
    box-shadow: 0 2px 2px 0 rgba(0, 63, 51, 0.15);

    @media (min-width: 768px) {
      box-shadow: 0 8px 8px -4px rgba(0, 63, 51, 0.12), 0 -24px 0 13px #fff;
      border: 1px solid var(--gray-300);
      border-radius: calc(var(--grid-unit) * 2) !important;
    }
  }

  &--authenticated {
    top: 0;

    @media (min-width: 768px) {
      top: calc(var(--nav-height) + var(--grid-unit));
    }

    &.opinion-bar--sticky {
      border-top-width: 0;

      @media (min-width: 768px) {
        border-top-width: 1px;
      }
    }
  }

  &--provider {
    @media (min-width: 768px) {
      top: var(--grid-unit);
    }
  }

  &__button {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0;
    color: var(--gray-almostBlack);
    background: none;
    border: none;

    & span {
      margin-left: calc(var(--grid-unit) / 2);

      /* stylelint-disable-next-line scss/at-extend-no-missing-placeholder */
      @extend .font-body-small !optional;
    }

    @media (min-width: 400px) {
      & span {
        font: inherit;
      }
    }

    &--favorited {
      color: var(--coral-80);
      background: var(--coral-0);

      @media (min-width: 400px) {
        border-top-left-radius: calc(var(--grid-unit) * 2);
        border-bottom-left-radius: inherit;
      }

      .fas {
        color: var(--coral-20);
      }
    }

    &--ignored {
      background: var(--gray-300);

      @media (min-width: 400px) {
        border-top-right-radius: calc(var(--grid-unit) * 2);
        border-bottom-right-radius: inherit;
      }

      .fas {
        color: var(--gray-500);
      }
    }

    &--saved {
      color: white;
      background: var(--sapphire-100);

      .fas {
        color: white;
      }
    }
  }
}

.opinion-bar--bottom {
  .opinion-bar__button--favorited {
    border-top-left-radius: 0;
    border-bottom-left-radius: calc(var(--grid-unit) * 2);
  }

  .opinion-bar__button--ignored {
    border-top-right-radius: 0;
    border-bottom-right-radius: calc(var(--grid-unit) * 2);
  }
}
</style>
